import React, { useState } from 'react';
import { Button, Menu } from '@mui/material';
import {
  AccountInfoItem,
  ApproveItem,
  ApproveItemLegacy,
  ArchiveItem,
  ArchiveItemLegacy,
  PhoneScreenPendingItem,
  PhoneScreenPendingItemLegacy,
  RejectItem,
  RejectItemLegacy,
  PhoneScreenScheduledItem,
  PhoneScreenScheduledItemLegacy,
  ResumeReviewItem,
} from './StatusItems';
import { useRecordContext, useShowContext, useUpdate, useNotify, useRefresh } from 'react-admin';
import { ProfessionalStatusEnum } from 'typings/enums';
import { ProfessionalDisplayHelper } from 'utils/displayHelpers/professional';
import { IProfessional } from '@medely/types';

export const AccountInfoStatus = 'account_info' as const;
export const ApprovedStatus = 'approved' as const;
export const ArchivedStatus = 'archived' as const;
export const PhoneScreenPendingStatus = 'phone_screen_pending' as const;
export const PhoneScreenScheduledStatus = 'phone_screen_scheduled' as const;
export const RejectedStatus = 'rejected' as const;
export const ResumeReviewStatus = 'resume_review' as const;

export type ApplicationStatus =
  | typeof AccountInfoStatus
  | typeof ApprovedStatus
  | typeof ArchivedStatus
  | typeof PhoneScreenPendingStatus
  | typeof PhoneScreenScheduledStatus
  | typeof RejectedStatus
  | typeof ResumeReviewStatus;

type ProfessionalScreeningData = {
  id?: number;
  professional_id?: number;
  reason?: string;
  scheduled_time?: string;
  screener_account_id?: number;
  screening_type?: 'phone' | 'written';
  canceled_at?: string;
};

type ProfessionalUpdateData = {
  application_status: ApplicationStatus;
  status?: ProfessionalStatusEnum;
  professional_screenings_attributes?: ProfessionalScreeningData;
  archive_reason?: string;
  reject_reason?: string;
};

// New flow (rescreening automation enabled)
const applicationStatusSelectionNew = {
  [ArchivedStatus]: [ResumeReviewItem, PhoneScreenPendingItem, RejectItem, AccountInfoItem],
  [ResumeReviewStatus]: [PhoneScreenPendingItem, ApproveItem, RejectItem, ArchiveItem],
  [PhoneScreenScheduledStatus]: [PhoneScreenPendingItem, ApproveItem, RejectItem, ArchiveItem],
  [PhoneScreenPendingStatus]: [PhoneScreenScheduledItem, ApproveItem, RejectItem, ArchiveItem],
  [RejectedStatus]: [PhoneScreenPendingItem],
};

// Legacy flow (rescreening automation disabled)
const applicationStatusSelectionLegacy = {
  [ArchivedStatus]: [
    ResumeReviewItem,
    PhoneScreenPendingItemLegacy,
    RejectItemLegacy,
    AccountInfoItem,
  ],
  [ResumeReviewStatus]: [
    PhoneScreenPendingItemLegacy,
    ApproveItemLegacy,
    RejectItemLegacy,
    ArchiveItemLegacy,
  ],
  [PhoneScreenScheduledStatus]: [
    PhoneScreenPendingItemLegacy,
    ApproveItemLegacy,
    RejectItemLegacy,
    ArchiveItemLegacy,
  ],
  [PhoneScreenPendingStatus]: [
    PhoneScreenScheduledItemLegacy,
    ApproveItemLegacy,
    RejectItemLegacy,
    ArchiveItemLegacy,
  ],
  [RejectedStatus]: [PhoneScreenPendingItemLegacy],
};

const getApplicationStatusSelection = (
  currentApplicationStatus: ApplicationStatus,
  isRescreeningEnabled: boolean,
) => {
  const selectionMap = isRescreeningEnabled
    ? applicationStatusSelectionNew
    : applicationStatusSelectionLegacy;
  const view = selectionMap[currentApplicationStatus];
  if (currentApplicationStatus === ApprovedStatus) {
    return [];
  }
  return view ?? [isRescreeningEnabled ? RejectItem : RejectItemLegacy];
};

const ApplicationStatusControl = () => {
  const { refetch } = useShowContext();
  const record = useRecordContext();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (!record) {
    return null;
  }

  const professionalType = new ProfessionalDisplayHelper(record as IProfessional).type;
  if (professionalType !== 'Marketplace') {
    return null;
  }

  if (!record.application_status) {
    return null;
  }
  const currentApplicationStatus = record.application_status;
  const isRescreeningEnabled = record?.enable_rescreening_automation || false;

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const updateApplicationStatus = (status) => {
    const applicationStatus = status?.application_status;
    const willBeApproved = applicationStatus === ApprovedStatus;

    if (!isRescreeningEnabled) {
      update(
        'Professional',
        { id: record.id, data: status },
        {
          onSuccess: () => {
            refetch?.();
            handleClose();
          },
          onError: (error: any) => {
            notify(error.message || 'An error occurred', { type: 'error' });
          },
        },
      );
      return;
    }

    const updateData: ProfessionalUpdateData = { application_status: applicationStatus };

    if (status?.archive_reason) {
      updateData.archive_reason = status.archive_reason;
    }
    if (status?.reject_reason) {
      updateData.reject_reason = status.reject_reason;
    }

    if (willBeApproved) {
      updateData.status = ProfessionalStatusEnum.active;
    } else if (status?.status) {
      updateData.status = status.status;
    }

    if (status?.cancel_existing_screening_id) {
      updateData.professional_screenings_attributes = {
        id: status.cancel_existing_screening_id,
        canceled_at: status.canceled_at,
      };
    } else {
      const professionalScreeningAttributes = {
        id: status?.id,
        professional_id: status?.professional_id,
        reason: status?.reason,
        scheduled_time: status?.scheduled_time,
        screener_account_id: status?.screener_account_id,
        screening_type: status?.screening_type,
        canceled_at: status?.canceled_at,
      };

      const hasProfessionalScreeningAttributes = Object.entries(professionalScreeningAttributes)
        .filter(([key]) => key !== 'professional_id')
        .some(([, value]) => value !== undefined);

      if (hasProfessionalScreeningAttributes) {
        updateData.professional_screenings_attributes = professionalScreeningAttributes;
      }
    }
    update(
      'Professional',
      { id: record.id, data: updateData },
      {
        onSuccess: () => {
          refetch?.();
          refresh();
          handleClose();
        },
        onError: (error: any) => {
          notify(error.message || 'An error occurred', { type: 'error' });
        },
      },
    );
  };

  const selections = getApplicationStatusSelection(currentApplicationStatus, isRescreeningEnabled);
  if (selections.length === 0) {
    return null;
  }

  return (
    <div>
      <Button size="small" variant="contained" onClick={handleClick} data-testid="actions-button">
        Actions ▼
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {selections.map((Item) => (
          <Item
            key={Item.name}
            currentApplicationStatus={currentApplicationStatus}
            updateApplicationStatus={updateApplicationStatus}
          />
        ))}
      </Menu>
    </div>
  );
};

export default ApplicationStatusControl;
